import { ChevronRight, Home } from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

/* ── Route → breadcrumb mapping ── */
const ROUTE_MAP: Record<string, { label: string; parent?: string }> = {
  "/": { label: "Dashboard" },
  "/crm": { label: "Prospects", parent: "/" },
  "/members": { label: "Members", parent: "/" },
  "/companies": { label: "Companies", parent: "/" },
  "/staff": { label: "Staff", parent: "/" },
  "/events": { label: "Events", parent: "/" },
  "/programs": { label: "Programs", parent: "/" },
  "/learning": { label: "Learning", parent: "/" },
  "/video-library": { label: "Video Library", parent: "/" },
  "/learning-hub": { label: "Learning Hub", parent: "/" },
  "/communications": { label: "Communications", parent: "/" },
  "/community": { label: "Community", parent: "/" },
  "/committees": { label: "Teams", parent: "/" },
  "/intelligence": { label: "Intelligence", parent: "/" },
  "/insights": { label: "Intelligence", parent: "/" },
  "/retention": { label: "Retention", parent: "/" },
  "/tiers": { label: "Membership Tiers", parent: "/" },
  "/billing": { label: "Billing", parent: "/" },
  "/sponsorship": { label: "Sponsorship", parent: "/" },
  "/reports": { label: "Reports", parent: "/" },
  "/website": { label: "Website", parent: "/" },
  "/settings": { label: "Settings", parent: "/" },
  "/admin": { label: "Admin", parent: "/" },
  "/master-admin": { label: "Platform Admin", parent: "/" },
};

const AppBreadcrumbs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [_searchParams] = useSearchParams();
  const path = location.pathname;

  // Don't show on dashboard
  if (path === "/") return null;

  // Build crumb chain
  const crumbs: { label: string; path: string }[] = [];

  // Special handling for member detail with from/section params
  const memberMatch = path.match(/^\/members\/([^/]+)$/);
  if (memberMatch) {
    // This is handled by MemberDetail's own breadcrumbs
    return null;
  }

  // Build standard breadcrumbs
  const basePath = `/${path.split("/").filter(Boolean)[0]}`;
  const route = ROUTE_MAP[basePath];

  if (!route) return null;

  // Always start with Dashboard
  crumbs.push({ label: "Dashboard", path: "/" });

  // Add parent nav group label if route is a child
  const parentGroup = getParentGroup(basePath);
  if (parentGroup) {
    crumbs.push({ label: parentGroup, path: basePath });
  }

  // Add current page
  crumbs.push({ label: route.label, path: basePath });

  // Deduplicate: if parent group label == route label, remove one
  const deduped = crumbs.filter((c, i) => i === 0 || c.label !== crumbs[i - 1]?.label);

  return (
    <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-4">
      {deduped.map((crumb, i) => {
        const isLast = i === deduped.length - 1;
        return (
          <span key={`${crumb.path}-${crumb.label}`} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="w-3 h-3" />}
            {isLast ? (
              <span className="text-foreground font-medium">{crumb.label}</span>
            ) : (
              <button
                type="button"
                onClick={() => navigate(crumb.path)}
                className="hover:text-foreground transition-colors flex items-center gap-1"
              >
                {i === 0 && <Home className="w-3 h-3" />}
                {crumb.label}
              </button>
            )}
          </span>
        );
      })}
    </nav>
  );
};

function getParentGroup(path: string): string | null {
  const groups: Record<string, string[]> = {
    Relationships: ["/crm", "/companies", "/members", "/staff"],
    Programming: ["/events", "/programs", "/learning"],
    Revenue: ["/tiers", "/billing", "/sponsorship"],
  };
  for (const [group, paths] of Object.entries(groups)) {
    if (paths.includes(path)) return group;
  }
  return null;
}

export default AppBreadcrumbs;
